import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ANALYSIS_CATEGORIES } from './categories';
import { useAnalysisEntitlement } from '../../hooks/analysis/useAnalysisEntitlement';
import SubscriptionBanner from './shared/SubscriptionBanner';
import './analysis-theme.css';
import './AnalysisLayout.css';

interface AnalysisLayoutProps {
  children: React.ReactNode;
}

/**
 * Shell for every analysis view: heading, subscription banner, and the
 * category tab strip (Links driving the nested routes). Tabs outside the
 * subscription's enabled categories render locked; unavailable (future)
 * categories are hidden from the strip entirely.
 */
const AnalysisLayout: React.FC<AnalysisLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { entitlement } = useAnalysisEntitlement();
  const enabled = new Set(entitlement.enabled_categories);

  return (
    <div className="analysis-root">
      <div className="analysis-header">
        <h1 className="analysis-title">
          <Link to="/analysis" className="analysis-title-link">📊 Analysis</Link>
        </h1>
        {entitlement.tier && (
          <span className="analysis-tier-chip">{entitlement.tier}</span>
        )}
      </div>

      <SubscriptionBanner entitlement={entitlement} />

      <nav className="analysis-tabs">
        {ANALYSIS_CATEGORIES.filter(c => c.available).map(category => {
          const path = `/analysis/${category.path}`;
          const isActive = location.pathname === path;
          const isLocked = !enabled.has(category.id);
          if (isLocked) {
            return (
              <span key={category.id} className="analysis-tab analysis-tab--locked" title="Upgrade to unlock">
                🔒 {category.label}
              </span>
            );
          }
          return (
            <Link
              key={category.id}
              to={path}
              className={`analysis-tab ${isActive ? 'analysis-tab--active' : ''}`}
            >
              {category.icon} {category.label}
            </Link>
          );
        })}
      </nav>

      {children}
    </div>
  );
};

export default AnalysisLayout;
